import { useEffect, useState } from "react";

// One definition of "mobile", read live.
//
// This used to be three: App.tsx's resize listener, a snapshot on the slider
// store written only when a template arrived, and a `.mobile` class the booking
// CSS depended on entirely. The store copy was the dangerous one — it never
// updated on resize, so a phone could keep a desktop layout and the booking
// panel's fixed 300px rail would blow out a 375px viewport with no media query
// underneath to catch it.
//
// The rule now: CSS owns layout (see the @media blocks in index.css, which are
// true regardless of what any store thinks), and this owns BEHAVIOUR — the
// decisions CSS cannot express, like whether to mount the immersive overlay at
// all. Both agree on one number.

export const MOBILE_BREAKPOINT = 768;

/** Live viewport check. Never cached — that was the bug. */
export const isMobileViewport = (): boolean =>
  typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;

/** The part of the page the guest can actually see, in layout-viewport CSS px. */
export type ViewportBox = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const readVisualViewport = (): ViewportBox | null => {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (!vv) return null;
  return {
    top: vv.offsetTop,
    left: vv.offsetLeft,
    width: vv.width,
    height: vv.height,
  };
};

const sameBox = (a: ViewportBox, b: ViewportBox) =>
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

/**
 * Tracks the visual viewport while `active`, null otherwise (and on browsers
 * without the API, which keep the plain 100% box).
 *
 * A position:fixed panel sized to 100% follows the LAYOUT viewport, and on a
 * phone that is not what the guest sees: iOS opens the keyboard by panning the
 * visual viewport over a layout viewport that keeps its full height, so the
 * header slides off the top or a strip of the host page shows above the
 * keyboard; Chrome on iOS draws its bottom toolbar over the input; a pinch
 * zoom pushes the send button off the right edge. Pinning the panel to this
 * box keeps header, conversation and input inside the visible area in all of
 * those cases.
 */
export const useVisualViewportBox = (active: boolean): ViewportBox | null => {
  const [box, setBox] = useState<ViewportBox | null>(() =>
    active ? readVisualViewport() : null
  );

  useEffect(() => {
    if (!active) {
      setBox(null);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;

    let frame = 0;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = readVisualViewport();
        setBox((prev) => (prev && next && sameBox(prev, next) ? prev : next));
      });
    };
    // Not every change of the visible area fires an event: on Android, Chrome
    // can move its bottom address bar after the keyboard has finished opening
    // without a visualViewport resize, which left the panel at the in-between
    // height with a strip of the site under it. Read again while things
    // settle after anything that moves the keyboard or the browser chrome.
    const settle = () => {
      update();
      [150, 400, 800].forEach((ms) => {
        const timer = setTimeout(() => {
          timers.delete(timer);
          update();
        }, ms);
        timers.add(timer);
      });
    };
    settle();
    vv.addEventListener("resize", settle);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", settle);
    document.addEventListener("focusin", settle);
    document.addEventListener("focusout", settle);
    return () => {
      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
      vv.removeEventListener("resize", settle);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", settle);
      document.removeEventListener("focusin", settle);
      document.removeEventListener("focusout", settle);
    };
  }, [active]);

  return box;
};

/**
 * Stops the host page from scrolling while `active`. Behind a fullscreen chat
 * on a phone the page is invisible, and letting it move only means a drag on
 * the header or an overscroll at the end of the conversation scrolls a site
 * the guest cannot see. The host's own inline values come back on release.
 */
export const useDocumentScrollLock = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const html = document.documentElement;
    const body = document.body;
    const previous = [html.style.overflow, body.style.overflow];
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = previous[0];
      body.style.overflow = previous[1];
    };
  }, [active]);
};
